import first from 'lodash/first'
import React, { ReactNode, useContext, VFC } from 'react'
import styled from 'styled-components'

import { Button } from '@Components/Button'
import { GoToCartLink } from '@Components/BuyButton/styledElements'
import { Modal, SharedModalProps } from '@Components/Modal'
import { DialogProps } from '@Components/Modal/Dialog'
import { CartErrorTypes } from '@Globals/Cart/useAddToCart'
import { MetadataContext } from '@Globals/Metadata'
import { modalFooter, modalMessage } from '@Globals/Style/mixins'
import { useTranslation } from '@Globals/Translations'

export const ModalTitle = styled.div`
  font-size: 1.5rem;
  margin-bottom: 10px;
`

export const ModalSubtitle = styled.div`
  font-size: 1.25rem;
`

export const DoubleEngravingText = styled.div`
  white-space: pre-wrap;
`

export const Message = styled.div`
  ${modalMessage}
`

export const Footer = styled.div`
  ${modalFooter}
  gap: 10px;
  align-items: center;
`

type ErrorModalProp = SharedModalProps & {
  extraArguments?: string[]
  message?: CartErrorTypes | string
}

export const ErrorModal: VFC<ErrorModalProp> = ({ extraArguments, isOpen, message, onHide }) => {
  const { t, tDanger } = useTranslation()
  const { storeURL } = useContext(MetadataContext)

  let content: ReactNode
  let modalVariant: DialogProps['variant'] = 'error'

  switch (message) {
    case 'DOUBLE_ENGRAVING':
      content = (
        <>
          <ModalTitle>{t('products:WE_COULD_NOT_ADD_THE_PRODUCT')}</ModalTitle>
          <DoubleEngravingText>{t('cart:DOUBLE_ENGRAVING_WARNING_TEXT')}</DoubleEngravingText>
        </>
      )
      break
    case 'REQUESTED_QUANTITY_NOT_IN_STOCK':
      content = (
        <>
          {first(extraArguments) && <ModalTitle><strong>{first(extraArguments)}</strong></ModalTitle>}
          <p {...tDanger('cart:PP_IS_UNFORTUNATELY_OUT_OF_STOCK_TEXT')} />
          <p>
            {t('cart:PP_ADJUST_AMOUNT_FROM_CART_TEXT')}
          </p>
        </>
      )
      modalVariant = 'sadError'
      break
    default:
      content = <ModalSubtitle>{t('common:SOMETHING_WENT_WRONG_PLEASE_CONTACT_US')}</ModalSubtitle>
  }

  const buttonText = message === 'DOUBLE_ENGRAVING' ? 'okay' : 'close'

  return (
    <Modal button={message === 'REQUESTED_QUANTITY_NOT_IN_STOCK' ? undefined : buttonText} isOpen={isOpen} onHide={onHide} variant={modalVariant}>
      <Message>{content}</Message>
      {message === 'REQUESTED_QUANTITY_NOT_IN_STOCK'
      && (
        <Footer>
          <Button color="dark" onClick={onHide} variant="outlined">{t('cart:PP_CONTINUE_SHOPPING_BUTTON_TEXT')}</Button>
          <GoToCartLink color="primary" newTab={false} text={t('cart:GO_TO_CART')} url={storeURL('checkout/cart')} />
        </Footer>
      )}
    </Modal>
  )
}
